import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

/** Janela de coalescência de eventos WS antes de um único refetch da Corrente. */
export const PAINEL_REFRESH_DEBOUNCE_MS = 800;

/**
 * usePainelLiveRefresh — mantém a Corrente "viva" sem martelar o banco.
 *
 * Assina SÓ os eventos que mudam a COMPOSIÇÃO da Corrente (prioridade, status,
 * modo de operação/handback da IA, conversa nova) — NÃO `MESSAGE_RECEIVED` (alta
 * frequência; a Corrente não mostra snippet). Coalesce rajadas num único
 * `invalidateQueries(['conversations'])` (atinge a lista da Corrente + a contagem
 * da fila do Pulso). NÃO invalida o agregador do dashboard (`agent-dashboard-state`),
 * protegendo as 7 sub-queries — o balanço H×IA fica no staleTime (≤30s).
 */
export function usePainelLiveRefresh(): void {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const scheduleRefresh = (): void => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        timer.current = null;
      }, PAINEL_REFRESH_DEBOUNCE_MS);
    };

    const unsubs = [
      subscribe(WS_EVENTS.CONVERSATION_PRIORITY_CHANGED, scheduleRefresh),
      subscribe(WS_EVENTS.CONVERSATION_STATUS_CHANGED, scheduleRefresh),
      subscribe(WS_EVENTS.OPERATION_MODE_CHANGED, scheduleRefresh),
      subscribe(WS_EVENTS.CONVERSATION_CREATED, scheduleRefresh),
    ];

    return () => {
      unsubs.forEach((u) => u());
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [queryClient, subscribe]);
}
